import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { useRecoveryStore } from '@lib/store';
import { PaletteUtils } from '@shared/utils';
import { Button, TextInput } from '@shared/widgets';

export interface IForm {
  username: string;
  card: string;
}
const DEFAULT_VALUES: IForm = { username: '', card: '' };

interface StepOneProps {
  onSubmit?: (data: IForm) => void;
}

export const StepOne = (props: StepOneProps) => {
  const recoveryStore = useRecoveryStore();

  const { control, formState, handleSubmit, watch, setError } = useForm({ defaultValues: DEFAULT_VALUES });
  const { t } = useTranslation('remember');

  const submitHandler = async (data: IForm) => {
    const result = await recoveryStore.recovery({ username: data.username, cardLast4Digits: data.card });
    if (result.error) {
      setError('card', { message: result.error });
      return;
    }
    props.onSubmit?.(data);
  };

  const isDisabled = !watch('username') || !watch('card');
  return (
    <form autoComplete='off' noValidate onSubmit={handleSubmit(submitHandler)}>
      <Controller
        name='username'
        control={control}
        rules={{ required: t('stepOne.errors.emptyUsername') }}
        render={({ field, fieldState: { error } }) => (
          <TextInput
            {...field}
            inputClassName={clsx(
              'hover:border-black transition-all ease duration-300',
              PaletteUtils.isBordierPalette ? 'bg-[#f8f8f8]' : 'bg-[#303034]',
            )}
            captionClassName='!text-base !font-light'
            caption={t('placeholders.username', { ns: 'common' })}
            name='username'
            required
            maxLength={30}
            error={error?.message}
          />
        )}
      />
      <Controller
        name='card'
        control={control}
        rules={{
          minLength: { value: 4, message: t('stepOne.errors.invalidCardNumber') },
          maxLength: { value: 4, message: t('stepOne.errors.invalidCardNumber') },
        }}
        render={({ field, fieldState: { error } }) => (
          <TextInput
            {...field}
            inputClassName={clsx(
              'hover:border-black transition-all ease duration-300',
              PaletteUtils.isBordierPalette ? 'bg-[#f8f8f8]' : 'bg-[#303034]',
            )}
            captionClassName='!text-base !font-light'
            name='card'
            caption={t('stepOne.placeholders.card')}
            required
            maxLength={4}
            inputPattern='\d'
            error={error?.message}
          />
        )}
      />
      <Button
        className='mt-[92px] text-base h-[50px]'
        fullWidth
        caption={t('buttons.next', { ns: 'common' })}
        type='submit'
        disabled={isDisabled || formState.isSubmitting}
      />
    </form>
  );
};
